import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import InputAdornment from '@mui/material/InputAdornment';

import { PageWithTitle } from 'src/Common/components';
import { usePositions } from '../context';
import { usePerformance, useCreateCashflow, usePositionsEnded, useUpdatePairThreshold } from '../api/usePositions';
import { PredictionPosition, PositionsGroupedByPair } from '../types';

const chipSx = { borderRadius: '4px' } as const;

const formatUsd = (value: number | undefined) => {
  if (value == null || isNaN(value)) return '-';
  return '$' + value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatPrice = (value: number | undefined) => {
  if (value == null || isNaN(value)) return '-';
  return value.toFixed(4);
};

const formatContracts = (value: number | undefined) => {
  if (value == null) return '-';
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const pnlColor = (pnl: number | undefined) => {
  if (pnl == null) return 'inherit';
  if (pnl > 0) return '#4caf50';
  if (pnl < 0) return '#f44336';
  return 'inherit';
};

type SortField = 'pnl' | 'net' | 'makerTna' | 'takerTna' | 'days' | 'hedge' | 'invested';
type SortDir = 'asc' | 'desc';

// Small labeled value
const LabeledValue = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <Box sx={{ textAlign: 'left', minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.5rem', color: 'grey.600', lineHeight: 1, textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.72rem', color: color || 'grey.300', whiteSpace: 'nowrap', lineHeight: 1.3, fontWeight: label === 'P&L' ? 600 : 400 }}>{value}</Typography>
  </Box>
);

// --- Position Cell (one platform side) ---

const PositionCell = ({ position, title }: { position: PredictionPosition; title?: string }) => {
  const invested = position.avgPrice * position.contracts;
  return (
    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
      {/* Title row */}
      <Stack direction="row" alignItems="center" spacing={0.3} sx={{ mb: 0.2 }}>
        {title && (
          <Typography
            variant="body2"
            sx={{ fontSize: '0.68rem', color: 'grey.400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}
            title={title}
          >
            {title}
          </Typography>
        )}
        {position.marketUrl && (
          <IconButton
            size="small"
            component="a"
            href={position.marketUrl}
            target="_blank"
            rel="noopener"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{ p: 0.1, flexShrink: 0 }}
          >
            <OpenInNewIcon sx={{ fontSize: '0.72rem', color: 'grey.500' }} />
          </IconButton>
        )}
      </Stack>
      {/* Data row */}
      <Stack direction="row" alignItems="flex-end" spacing={1}>
        <Chip
          label={position.side.toUpperCase()}
          size="small"
          color={position.side === 'yes' ? 'success' : 'error'}
          sx={{ ...chipSx, fontSize: '0.58rem', fontWeight: 700, height: 17, minWidth: 30 }}
        />
        <LabeledValue label="AVG" value={formatPrice(position.avgPrice)} />
        <LabeledValue label="BID" value={formatPrice(position.topBid)} color="#64b5f6" />
        <LabeledValue label="ASK" value={formatPrice(position.topAsk)} color="#ffb74d" />
        <LabeledValue label="QTY" value={formatContracts(position.contracts)} color="#b0bec5" />
        <LabeledValue label="INVESTED" value={formatUsd(invested)} />
        <LabeledValue label="P&L" value={formatUsd(position.unrealizedPnl)} color={pnlColor(position.unrealizedPnl)} />
      </Stack>
    </Box>
  );
};

const EmptyPositionCell = () => (
  <Typography variant="body2" sx={{ fontSize: '0.72rem', color: 'grey.700', py: 1 }}>—</Typography>
);

// --- Stats column widths ---
const statW = { invested: 75, pnl: 65, hedge: 70, days: 28, mtna: 55, ttna: 55, net: 75, threshold: 62 };

// --- Threshold Input ---

const ThresholdInput = ({ pairId, current, onConfirm }: { pairId: string; current: number; onConfirm: (pairId: string, value: number) => void }) => {
  const [val, setVal] = useState(current > 0 ? String(current) : '');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1, width: statW.threshold, ml: 0.5 }}>
      <TextField
        size="small"
        placeholder="0"
        value={val}
        onChange={e => setVal(e.target.value)}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => { if (e.key === 'Enter') { const n = parseFloat(val); onConfirm(pairId, isNaN(n) ? 0 : n); } }}
        InputProps={{
          startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '0.5rem', color: 'grey.600' }, minWidth: 0, mr: -0.3 }}>$</InputAdornment>,
        }}
        sx={{
          flex: 1,
          '& .MuiInputBase-input': { fontSize: '0.62rem', py: 0.15, px: 0.2, MozAppearance: 'textfield', '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { display: 'none' } },
          '& .MuiOutlinedInput-root': { height: 20, minHeight: 20 },
        }}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
      />
      <IconButton
        size="small"
        onClick={e => { e.stopPropagation(); const n = parseFloat(val); onConfirm(pairId, isNaN(n) ? 0 : n); }}
        sx={{ p: 0.1, color: 'grey.500', '&:hover': { color: 'success.main' } }}
      >
        <CheckIcon sx={{ fontSize: '0.65rem' }} />
      </IconButton>
    </Box>
  );
};

// --- Pair Row (3 columns: poly | kalshi | stats) ---

const PairRow = ({ group, onThresholdConfirm }: { group: PositionsGroupedByPair; onThresholdConfirm?: (pairId: string, value: number) => void }) => {
  const totalPnl = group.totalUnrealizedPnl ?? 0;
  const net = group.netContracts ?? 0;
  const hedgePnl = group.hedgePnl;
  const polyPos = group.positions.find(p => p.platform === 'polymarket');
  const kalshiPos = group.positions.find(p => p.platform === 'kalshi');
  const isUnmatched = !polyPos || !kalshiPos;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        py: 0.4,
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' },
        ...(isUnmatched && { backgroundColor: 'rgba(255,152,0,0.08)' }),
      }}
    >
      {/* Polymarket */}
      <Box sx={{ flex: '1 1 33.33%', minWidth: 0, pr: 1 }}>
        {polyPos ? <PositionCell position={polyPos} title={group.polymarketTitle || polyPos.pairName} /> : <EmptyPositionCell />}
      </Box>

      <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* Kalshi */}
      <Box sx={{ flex: '1 1 33.33%', minWidth: 0, px: 1 }}>
        {kalshiPos ? <PositionCell position={kalshiPos} title={group.kalshiTitle || kalshiPos.pairName} /> : <EmptyPositionCell />}
      </Box>

      <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* Stats */}
      <Stack direction="row" alignItems="center" sx={{ flex: '0 0 auto', pl: 1 }}>
        {isUnmatched ? (
          <Box sx={{ width: statW.invested, display: 'flex', justifyContent: 'center' }}>
            <Chip
              label="UNMATCHED"
              size="small"
              sx={{
                ...chipSx,
                fontSize: '0.5rem',
                height: 16,
                fontWeight: 700,
                backgroundColor: 'rgba(255,152,0,0.25)',
                color: '#ff9800',
                border: '1px solid rgba(255,152,0,0.4)',
              }}
            />
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.72rem', color: 'grey.300', width: statW.invested, textAlign: 'right' }}>
            {formatUsd(group.totalInvested)}
          </Typography>
        )}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, width: statW.pnl, textAlign: 'right', color: pnlColor(totalPnl) }}>
          {formatUsd(totalPnl)}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, width: statW.hedge, textAlign: 'right', color: hedgePnl != null ? pnlColor(hedgePnl) : 'grey.600' }}>
          {hedgePnl != null ? formatUsd(hedgePnl) : '-'}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', width: statW.days, textAlign: 'right', fontWeight: group.daysToExpiry != null && group.daysToExpiry <= 5 ? 700 : 400, color: group.daysToExpiry != null && group.daysToExpiry <= 5 ? '#ff9800' : 'grey.500' }}>
          {group.daysToExpiry != null ? `${group.daysToExpiry < 1 ? group.daysToExpiry.toFixed(1) : Math.round(group.daysToExpiry)}d` : '-'}
        </Typography>
        <Typography sx={{
          fontSize: '0.68rem', width: statW.mtna, textAlign: 'right', fontWeight: 600,
          color: group.makerTna != null ? (group.makerTna > 0 ? '#4caf50' : '#f44336') : 'grey.600',
        }}>
          {group.makerTna != null ? `${group.makerTna.toFixed(1)}%` : '-'}
        </Typography>
        <Typography sx={{
          fontSize: '0.68rem', width: statW.ttna, textAlign: 'right', fontWeight: 600,
          color: group.takerTna != null ? (group.takerTna > 0 ? '#4caf50' : '#f44336') : 'grey.600',
        }}>
          {group.takerTna != null ? `${group.takerTna.toFixed(1)}%` : '-'}
        </Typography>
        <Typography sx={{
          fontSize: '0.72rem', fontWeight: 700, width: statW.net, textAlign: 'right',
          color: net > 0 ? '#4caf50' : net < 0 ? '#f44336' : 'grey.400',
        }}>
          {net > 0 ? '+' : ''}{formatContracts(net)} net
        </Typography>
        {group._id && onThresholdConfirm ? (
          <ThresholdInput pairId={group._id} current={group.maxInvestmentThreshold ?? 0} onConfirm={onThresholdConfirm} />
        ) : (
          <Box sx={{ width: statW.threshold + 4, ml: 0.5 }} />
        )}
      </Stack>
    </Box>
  );
};

// --- Ended Row ---

const EndedRow = ({ group }: { group: PositionsGroupedByPair }) => {
  const totalPnl = group.totalUnrealizedPnl ?? 0;
  return (
    <Box
      sx={{
        px: 2, py: 0.6,
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' }
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="body2"
          sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'grey.400', flex: 1, minWidth: 0 }}
          noWrap title={group.pairName}
        >
          {group.pairName || 'Unknown'}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
          {group.positions.map(p => (
            <Stack key={p._id} direction="row" alignItems="center" spacing={0.5}>
              <img
                src={`/assets/exchanges/png/100x100/${p.platform}.png`}
                alt={p.platform}
                style={{ width: 16, height: 16, borderRadius: '50%' }}
              />
              <Chip
                label={p.side.toUpperCase()}
                size="small"
                color={p.side === 'yes' ? 'success' : 'error'}
                sx={{ ...chipSx, fontSize: '0.55rem', fontWeight: 700, height: 16, minWidth: 28 }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.68rem', color: 'grey.500' }}>{formatPrice(p.avgPrice)}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.68rem', color: '#b0bec5' }}>{formatContracts(p.contracts)}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.68rem', color: 'grey.400' }}>{formatUsd(p.avgPrice * p.contracts)}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.68rem', fontWeight: 600, color: pnlColor(p.unrealizedPnl) }}>{formatUsd(p.unrealizedPnl)}</Typography>
            </Stack>
          ))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: 'rgba(255,255,255,0.08)' }} />
          <Chip label={formatUsd(group.totalInvested)} size="small" variant="outlined" sx={{ ...chipSx, fontSize: '0.6rem', height: 20, borderColor: 'grey.700' }} />
          <Chip
            label={formatUsd(totalPnl)} size="small" variant="outlined"
            sx={{ ...chipSx, fontSize: '0.6rem', height: 20, fontWeight: 600, borderColor: pnlColor(totalPnl), color: pnlColor(totalPnl) }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

// --- Sort Header Button ---

const SortHeader = ({
  label, field, width, activeField, activeDir, onClick,
}: {
  label: string; field: SortField; width: number; activeField: SortField; activeDir: SortDir; onClick: (f: SortField) => void;
}) => {
  const isActive = activeField === field;
  const Icon = isActive && activeDir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
  return (
    <Box
      onClick={() => onClick(field)}
      sx={{
        width, textAlign: 'right', cursor: 'pointer', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.2,
        '&:hover': { color: 'grey.300' },
      }}
    >
      <Typography sx={{ fontSize: '0.62rem', color: isActive ? 'grey.300' : 'grey.600', fontWeight: isActive ? 700 : 400, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Icon sx={{ fontSize: '0.6rem', color: isActive ? 'grey.300' : 'grey.700' }} />
    </Box>
  );
};

// --- Header Row (3 columns) ---

const HeaderRow = ({
  sortField, sortDir, onSort,
}: {
  sortField: SortField; sortDir: SortDir; onSort: (f: SortField) => void;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
    <Box sx={{ flex: '1 1 33.33%' }}>
      <Typography sx={{ fontSize: '0.7rem', color: 'grey.500', fontWeight: 600 }}>Polymarket</Typography>
    </Box>
    <Box sx={{ width: 1 }} />
    <Box sx={{ flex: '1 1 33.33%', pl: 1 }}>
      <Typography sx={{ fontSize: '0.7rem', color: 'grey.500', fontWeight: 600 }}>Kalshi</Typography>
    </Box>
    <Box sx={{ width: 1 }} />
    <Stack direction="row" alignItems="center" sx={{ flex: '0 0 auto', pl: 1 }}>
      <SortHeader label="INVESTED" field="invested" width={statW.invested} activeField={sortField} activeDir={sortDir} onClick={onSort} />
      <SortHeader label="P&L" field="pnl" width={statW.pnl} activeField={sortField} activeDir={sortDir} onClick={onSort} />
      <SortHeader label="HEDGE" field="hedge" width={statW.hedge} activeField={sortField} activeDir={sortDir} onClick={onSort} />
      <SortHeader label="Dn" field="days" width={statW.days} activeField={sortField} activeDir={sortDir} onClick={onSort} />
      <SortHeader label="MTNA" field="makerTna" width={statW.mtna} activeField={sortField} activeDir={sortDir} onClick={onSort} />
      <SortHeader label="TTNA" field="takerTna" width={statW.ttna} activeField={sortField} activeDir={sortDir} onClick={onSort} />
      <SortHeader label="Net" field="net" width={statW.net} activeField={sortField} activeDir={sortDir} onClick={onSort} />
      <Typography sx={{ fontSize: '0.62rem', color: 'grey.600', textTransform: 'uppercase', width: statW.threshold, textAlign: 'center' }}>
        Max $
      </Typography>
    </Stack>
  </Box>
);

// --- Cashflow Dialog ---

const CashflowDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [platform, setPlatform] = useState<'kalshi' | 'polymarket'>('kalshi');
  const [type, setType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const { mutate: create, isLoading } = useCreateCashflow();

  const handleSubmit = () => {
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) return;
    create(
      { platform, type, amountUsd: num, note: note || undefined, date },
      { onSuccess: () => { setAmount(''); setNote(''); onClose(); } }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '0.95rem' }}>Add Deposit / Withdrawal</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <FormControl size="small" fullWidth>
            <InputLabel>Platform</InputLabel>
            <Select value={platform} label="Platform" onChange={e => setPlatform(e.target.value as 'kalshi' | 'polymarket')}>
              <MenuItem value="kalshi">Kalshi</MenuItem>
              <MenuItem value="polymarket">Polymarket</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={type} label="Type" onChange={e => setType(e.target.value as 'deposit' | 'withdrawal')}>
              <MenuItem value="deposit">Deposit</MenuItem>
              <MenuItem value="withdrawal">Withdrawal</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Amount (USD)" type="number" value={amount} onChange={e => setAmount(e.target.value)} fullWidth />
          <TextField size="small" label="Date" type="date" value={date} onChange={e => setDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Note (optional)" value={note} onChange={e => setNote(e.target.value)} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" size="small" disabled={isLoading || !amount}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Main Page ---

export const PositionsPage = () => {
  const [tab, setTab] = useState(0);
  const [sortField, setSortField] = useState<SortField>('pnl');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [cashflowOpen, setCashflowOpen] = useState(false);
  const { groups, unmatched, stats, isConnected, triggerSync, isSyncing } = usePositions();
  const { data: performance } = usePerformance();
  const { data: endedGroups } = usePositionsEnded();
  const { mutate: updateThreshold } = useUpdatePairThreshold();

  const handleThresholdConfirm = useCallback((pairId: string, value: number) => {
    updateThreshold({ pairId, threshold: value });
  }, [updateThreshold]);

  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  }, [sortField]);

  // Merge matched + unmatched, sort together
  const allOpenGroups = useMemo(() => {
    const all = [...groups, ...unmatched];
    const mult = sortDir === 'desc' ? -1 : 1;
    all.sort((a, b) => {
      if (sortField === 'invested') return mult * ((a.totalInvested ?? 0) - (b.totalInvested ?? 0));
      if (sortField === 'pnl') return mult * ((a.totalUnrealizedPnl ?? 0) - (b.totalUnrealizedPnl ?? 0));
      if (sortField === 'hedge') return mult * ((a.hedgePnl ?? 0) - (b.hedgePnl ?? 0));
      if (sortField === 'days') return mult * ((a.daysToExpiry ?? 9999) - (b.daysToExpiry ?? 9999));
      if (sortField === 'makerTna') return mult * ((a.makerTna ?? 0) - (b.makerTna ?? 0));
      if (sortField === 'takerTna') return mult * ((a.takerTna ?? 0) - (b.takerTna ?? 0));
      return mult * (Math.abs(a.netContracts ?? 0) - Math.abs(b.netContracts ?? 0));
    });
    return all;
  }, [groups, unmatched, sortField, sortDir]);

  const totalHedgePnl = useMemo(() => {
    let sum = 0; let hasAny = false;
    for (const g of groups) { if (g.hedgePnl != null) { sum += g.hedgePnl; hasAny = true; } }
    return hasAny ? sum : null;
  }, [groups]);

  const endedTotalPnl = useMemo(() => {
    if (!endedGroups) return 0;
    return endedGroups.reduce((s, g) => s + (g.totalUnrealizedPnl ?? 0), 0);
  }, [endedGroups]);

  const openCount = groups.length + unmatched.length;

  return (
    <PageWithTitle>
      <Box px={2} py={1} width="100%">
        {/* Stats bar */}
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 'auto' }}>
            <Typography variant="h6" color="text.primary" sx={{ fontSize: '1.1rem' }}>Positions</Typography>
            <FiberManualRecordIcon sx={{ fontSize: 8, color: isConnected ? '#4caf50' : '#f44336' }} />
          </Stack>
            {stats && (
              <>
                <Chip label={`Open: ${stats.open}`} size="small" color="primary" variant="outlined" sx={chipSx} />
                <Chip label={`Invested: ${formatUsd(stats.totalInvested)}`} size="small" variant="outlined" sx={chipSx} />
                <Chip label={`P&L: ${formatUsd(stats.totalUnrealizedPnl)}`} size="small" variant="outlined"
                  sx={{ ...chipSx, borderColor: pnlColor(stats.totalUnrealizedPnl), color: pnlColor(stats.totalUnrealizedPnl) }} />
              </>
            )}
            {totalHedgePnl != null && (
              <Chip label={`Hedge: ${formatUsd(totalHedgePnl)}`} size="small" variant="outlined"
                sx={{ ...chipSx, borderColor: pnlColor(totalHedgePnl), color: pnlColor(totalHedgePnl) }} />
            )}
            {performance && (
              <>
                <Chip label={`Portfolio: ${formatUsd(performance.pnlUsd)}`} size="small" variant="outlined"
                  sx={{ ...chipSx, borderColor: pnlColor(performance.pnlUsd), color: pnlColor(performance.pnlUsd) }} />
                <Tooltip title={`Positions: ${formatUsd(performance.kalshi.portfolioValueUsd)} | Balance: ${formatUsd(performance.kalshi.balanceUsd)}`}>
                  <Chip label={`Kalshi: ${formatUsd(performance.kalshi.totalUsd)}`} size="small" variant="outlined" sx={chipSx} />
                </Tooltip>
                <Tooltip title={`Positions: ${formatUsd(performance.polymarket.positionsValueUsd)} | Cash: ${formatUsd(performance.polymarket.cashBalanceUsd)}`}>
                  <Chip label={`Poly: ${formatUsd(performance.polymarket.totalUsd)}`} size="small" variant="outlined" sx={chipSx} />
                </Tooltip>
                <Tooltip
                  title={
                    `Deposits: ${formatUsd(performance.cashflows.depositsUsd)}` +
                    (performance.cashflows.withdrawalsUsd > 0 ? ` | Withdrawals: ${formatUsd(performance.cashflows.withdrawalsUsd)}` : '')
                  }
                >
                  <Chip label={`Current: ${formatUsd(performance.currentValueUsd)}`} size="small" variant="outlined" sx={chipSx} />
                </Tooltip>
                <Chip label={`Baseline: ${formatUsd(performance.baselineValueUsd)}`} size="small" variant="outlined" sx={{ ...chipSx, borderColor: 'grey.700' }} />
              </>
            )}
            <IconButton size="small" onClick={() => setCashflowOpen(true)}
              sx={{ border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', width: 24, height: 24 }}>
              <AddIcon sx={{ fontSize: '0.85rem' }} />
            </IconButton>
            <Button variant="outlined" size="small"
              startIcon={isSyncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={triggerSync} disabled={isSyncing} sx={{ borderRadius: '4px' }}>
              SYNC
            </Button>
        </Stack>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ mb: 1, minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0, textTransform: 'none', fontSize: '0.82rem' }, '& .MuiTabs-indicator': { height: 2 } }}>
          <Tab label={`Open (${openCount})`} />
          <Tab label={`Ended (${endedGroups?.length ?? 0})`} />
        </Tabs>

        {/* Open Tab */}
        {tab === 0 && (
          <Box sx={{ background: 'background.paper', borderRadius: '4px', overflow: 'hidden' }}>
            <HeaderRow sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            {allOpenGroups.length === 0 ? (
              <Box display="flex" justifyContent="center" py={3}>
                <Typography color="text.secondary" variant="body2">No open positions</Typography>
              </Box>
            ) : (
              allOpenGroups.map((group, i) => <PairRow key={group._id ?? `open-${i}`} group={group} onThresholdConfirm={handleThresholdConfirm} />)
            )}
          </Box>
        )}

        {/* Ended Tab */}
        {tab === 1 && (
          <Box sx={{ background: 'background.paper', borderRadius: '4px', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 0.5, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'grey.600' }}>Ended Positions</Typography>
                <Chip label={`Total P&L: ${formatUsd(endedTotalPnl)}`} size="small" variant="outlined"
                  sx={{ ...chipSx, fontSize: '0.65rem', height: 20, fontWeight: 600, borderColor: pnlColor(endedTotalPnl), color: pnlColor(endedTotalPnl) }} />
              </Stack>
            </Box>
            {!endedGroups || endedGroups.length === 0 ? (
              <Box display="flex" justifyContent="center" py={3}>
                <Typography color="text.secondary" variant="body2">No ended positions</Typography>
              </Box>
            ) : (
              endedGroups.map((group, i) => <EndedRow key={group._id ?? `ended-${i}`} group={group} />)
            )}
          </Box>
        )}

        <CashflowDialog open={cashflowOpen} onClose={() => setCashflowOpen(false)} />
      </Box>
    </PageWithTitle>
  );
};
